<!-- =========================================================================================
  File Name: RequestSteps.vue
  Description: Show request steps
========================================================================================== -->

<template>
	<div class="request-steps">
		<PerfectScrollbar ref="requestStepsRef" :options="options">
			<ul class="steps-container">
				<li
					:key="item.id"
					v-for="(item, index) in value"
					:style="getCustomStyle(item, index)"
					:class="[
						'step-item',
						{
							'step-item-short': isShort,
							'step-item--active': activeId == item.id && !item.color,
							'step-item-blink': item.status == $constant.REQUEST_STATUSES.WAITING && checkUpdatedAt(),
						}
					]"
					@click.prevent="handleClick(item)"
				>
					<span class="step-item__name">{{ !isShort ? item.name : shortName(item.name, index) }}</span>
				</li>
			</ul>
		</PerfectScrollbar>
	</div>
</template>

<script>
import _ from "lodash";

const isValidStatusProp = status => {
	if (!status) {
		return true;
	}

	return (
		status &&
		[
			this.$constant.REQUEST_STATUSES.WAITING,
			this.$constant.REQUEST_STATUSES.REJECTED,
			this.$constant.REQUEST_STATUSES.CANCELED,
			this.$constant.REQUEST_STATUSES.APPROVED
		].includes(status)
	);
};

export default {
	name: "RequestSteps",
	inheritAttrs: false,
	props: {
		isShort: {
			type: Boolean,
			default: false
		},
		updatedAt: {
			type: String,
			default: null
		},
		value: {
			type: Array,
			required: true,
			validator: prop => {
				if (!_.isEmpty(prop)) {
					return !_.some(prop, obj => {
						if (typeof obj !== "object") {
							return true;
						}
						return (
							!_.has(obj, "id") ||
							!_.has(obj, "name") ||
							!_.has(obj, "status") ||
							!isValidStatusProp.bind(obj.status)
						);
					});
				}

				return true;
			}
		},
		activeId: {
			type: [Number, String]
		},
		disabledIds: {
			type: Array,
			validator: prop => {
				return !_.some(prop, item => {
					if (typeof item !== "number" && typeof item !== "string") {
						return true;
					}
				});
			}
		}
	},
	emits: {
		onClick: {}
	},
	data() {
		return {
			options: {
				suppressScrollY: true
			}
		};
	},
	methods: {
		checkUpdatedAt() {
			try {
				if (this.updatedAt) {
					let d = new Date(this.updatedAt);
					let hours = Math.abs(new Date() - d) / 36e5;
					if (hours >= 8) {
						return true;
					}
				}
			} catch (e) {
				console.log(e.message);
			}
			return false;
		},
		getCustomStyle(item, index) {
			let styleObj = {zIndex: 50 - index, opacity: 0.2, '--custom-color': 'white'};
			if (item.color) {
				styleObj['--custom-bg-color'] = item.color;
				// styleObj['--custom-color'] = 'black';
			} else {
				styleObj['--custom-bg-color'] = '#2B59B2';
			}
			if (!index) {
				styleObj['--custom-bg-color'] = '#AF00CC';
			}
			else if ((index == this.value.length - 1)) {
				styleObj['--custom-bg-color'] = '#1DD100';
				if (item.status == this.$constant.REQUEST_STATUSES.APPROVED) {
					styleObj.opacity = 1;
				}
			}
			if (item.status == this.$constant.REQUEST_STATUSES.REJECTED) {
				styleObj['--custom-bg-color'] = '#EB5757';
				styleObj.opacity = 1;
			} else if (item.status == this.$constant.REQUEST_STATUSES.CANCELED) {
				styleObj['--custom-bg-color'] = '#4A4A4A';
				styleObj.opacity = 1;
			} else if (item.status == this.$constant.REQUEST_STATUSES.WAITING) {
				styleObj.opacity = 1;
			}
			return styleObj;
		},
		shortName(str, index) {
			// if (!index || (index == this.value.length - 1)) {
			// 	return str;
			// }
			let s = str.split(' ').map(x => x[0]).join('');
			if (s) {
				s = s.toUpperCase();
			}
			return s;
		},
		handleClick(item) {
			if (!this.disabledIds.includes(item.id)) {
				this.$emit("onClick", item);
			}
		},
		autoScrollToActiveStep() {
			const requestSteps = document.querySelector(".request-steps");
			const requestActiveStep = document.querySelector(
				".step-item--active"
			);
			if (
				this.$refs.requestStepsRef &&
				requestSteps &&
				requestActiveStep &&
				requestActiveStep.offsetLeft + requestActiveStep.scrollWidth >
					requestSteps.scrollWidth
			) {
				this.$refs.requestStepsRef.$el.scrollLeft =
					requestActiveStep.offsetLeft;
			}
		}
	},
	mounted() {
		this.autoScrollToActiveStep();
	},
	updated() {
		this.autoScrollToActiveStep();
	}
};
</script>

<style lang="scss" scoped>
@keyframes fadeIn { 
  from { opacity: 0; } 
}
.request-steps {
	.steps-container {
		display: flex;
		align-items: center;
		list-style: none;
		.step-item-blink {
			-webkit-animation: fadeIn 1s alternate infinite;
			-moz-animation: fadeIn 1s alternate infinite;
			animation: fadeIn 1s alternate infinite;

			// animation: fadeIn 1s infinite alternate;
		}
		.step-item-short {
			height: 28px !important;
			width: 56px !important;
			padding: 0 0 0 5px !important;
			color: white;
			font-weight: bold;
			&::after,
			&::before {
				content: "";
				// border-left: 5px solid #fff !important;
				border-top: 14px solid transparent !important;
				border-bottom: 14px solid transparent !important;
			}
		}
		.step-item {
			font-weight: bold;
			display: flex;
			justify-content: center;
			align-items: center;
			flex: 1;
			height: 56px;
			position: relative;
			margin-left: 5px;
			padding: 0 0 0 10px;
			// background-color: var(--color-gray-5);
			background-color: var(--custom-bg-color);
			opacity: 0.5;
			transition: var(--common-transition);
			// cursor: not-allowed;
			cursor: pointer;
			// border-left-color: var(--color-gray-5);
			border-left-color: var(--custom-bg-color);

			&::after,
			&::before {
				content: "";
				border-left: 10px solid #fff;
				border-top: 28px solid transparent;
				border-bottom: 28px solid transparent;
				position: absolute;
				top: 0;
			}

			&::after {
				left: 100%;
				z-index: 20;
				// border-left-color: var(--color-gray-5);
				border-left-color: var(--custom-bg-color);
			}

			&::before {
				left: 0;
				z-index: 10;
			}

			&:first-child {
				margin-left: 0;

				&::before {
					display: none;
				}
			}

			&:last-child {
				&::after {
					display: none;
				}
			}

			&.step-item--waiting {
				background-color: var(--color-solid-primary);

				&::after {
					border-left-color: var(--color-solid-primary);
				}
			}

			&.step-item--rejected {
				background-color: var(--color-solid-red);

				&::after {
					border-left-color: var(--color-solid-red);
				}
			}

			&.step-item--completed {
				background-color: var(--color-solid-green-1);

				&::after {
					border-left-color: var(--color-solid-green-1);
				}
			}

			&.step-item--canceled {
				background-color: var(--color-solid-gray-1);

				&::after {
					border-left-color: var(--color-solid-gray-1);
				}
			}

			&.step-item--active {
				opacity: 1;
			}

			.step-item__name {
				// color: var(--color-gray-3);
				color: var(--custom-color);
				word-break: break-word;
				line-height: normal;
				margin: 0 10px;
				white-space: nowrap;
			}

			&.step-item--waiting,
			&.step-item--rejected,
			&.step-item--completed,
			&.step-item--canceled {
				cursor: pointer;

				.step-item__name {
					color: #fff;
				}
			}
		}
	}

	.ps {
		padding-bottom: 15px;
	}

	::v-deep .ps__rail-y {
		display: none;
	}
}
</style>
